import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ReservationFormPrivateRoomSection } from './ReservationFormPrivateRoomSection';
import { ROUTE_PATH } from '../../constants/routes';
import { API_ENDPOINTS, useApiRequest } from '../../hooks/useApiRequest';
import { ErrorNotice } from '../common/ErrorNotice';
import type { ReservationFormData } from './ReservationFormServiceTypeSelector';
import type { API } from '@/api';
import type { ReservationRestoreData } from '@/hooks/useRestoreReservation';
import { AIRPORTS, FLIGHT_TYPES, LOUNGES, TERMINALS } from '@/constants/area';
import { SPACE_TYPE } from '@/constants/space';
import { useApiErrorTranslation } from '@/hooks/useApiErrorTranslation';
import { useAreas } from '@/hooks/useAreas';
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat';
import { toISO8601 } from '@/utils/datetime';

type PrivateRoomSpaceSlots =
  API['GET']['PrivateRoomAvailableTimeSlots']['response']['spaces'];

interface Props {
  onActiveReservationChange?: (hasActive: boolean) => void;
  restoreData?: ReservationRestoreData;
}

export const ReservationFormPrivateRoom = ({
  onActiveReservationChange,
  restoreData: initialRestoreData,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { translateApiError } = useApiErrorTranslation();
  const [spaces, setSpaces] = useState<PrivateRoomSpaceSlots>([]);
  const [remainingMinutes, setRemainingMinutes] = useState<number | undefined>(
    undefined,
  );
  const [hasActiveReservation, setHasActiveReservation] = useState(false);
  const { formatDateWithWeekday } = useDateTimeFormat();
  const { fetchLounges, lounges } = useAreas();

  const { handleSubmit, watch, setValue } = useForm<ReservationFormData>({
    mode: 'onChange',
    defaultValues: {
      serviceType: SPACE_TYPE.PRIVATE_ROOM,
      airport: AIRPORTS.HANEDA,
      flightType: FLIGHT_TYPES.DOMESTIC,
      terminal: TERMINALS.TERMINAL2,
      lounge: LOUNGES.SUITE_LOUNGE,
      areaIds: [],
      boardingDate: undefined,
    },
  });

  // 初期化時にラウンジ情報を取得してareaIdsを設定
  useEffect(() => {
    fetchLounges(
      SPACE_TYPE.PRIVATE_ROOM,
      AIRPORTS.HANEDA,
      FLIGHT_TYPES.DOMESTIC,
      TERMINALS.TERMINAL2,
    );
  }, [fetchLounges]);

  const areaIds = watch('areaIds');

  // ラウンジ情報が取得できたらareaIdsを設定
  useEffect(() => {
    const suiteLounge = lounges.find((l) => l.lounge === LOUNGES.SUITE_LOUNGE);
    if (suiteLounge && areaIds.length === 0) {
      setValue('areaIds', suiteLounge.areaIds);
    }
  }, [lounges, setValue, areaIds]);

  // 未完了予約の状態を親コンポーネントに通知
  useEffect(() => {
    onActiveReservationChange?.(hasActiveReservation);
  }, [hasActiveReservation, onActiveReservationChange]);

  // スペース選択時にfloorを設定（ReservationFormPrivateRoomSectionから渡される）
  const [loungeFloor, setLoungeFloor] = useState<number | null>(null);

  const boardingDate = watch('boardingDate');
  const selectedStartTime = watch('selectedStartTime');
  const selectedEndTime = watch('selectedEndTime');

  const [isSectionOpen, setIsSectionOpen] = useState(true);
  const isSectionComplete = !!selectedEndTime;

  const confirmedDateStr: string = useMemo(() => {
    if (!boardingDate || !selectedStartTime || !selectedEndTime) {
      return '';
    }

    // 日付と曜日の部分を取得（例: 2025年11月24日（月））
    const dateWithWeekday = formatDateWithWeekday(boardingDate);

    // 「2025年11月24日（月） 20:05～21:00」形式で返す
    return `${dateWithWeekday} ${selectedStartTime}${t('reservation.confirm.timeRange')}${selectedEndTime}`;
  }, [
    boardingDate,
    selectedStartTime,
    selectedEndTime,
    formatDateWithWeekday,
    t,
  ]);

  const { execute: fetchPrivateRoomTimeSlots } = useApiRequest(
    'GET',
    API_ENDPOINTS.GET.PRIVATE_ROOM_AVAILABLE_TIME_SLOTS,
  );

  const [errorKey, setErrorKey] = useState<string | null>(null);

  // 日付選択時に時間帯を取得するハンドラー
  const handleDateSelect = useCallback(
    async (
      date: `${number}-${number}-${number}`,
      overrideAreaIds?: number[],
    ) => {
      setValue('boardingDate', date);
      // API呼び出し前に状態をリセット（ローディング中に古いデータが見えないようにする）
      setSpaces([]);
      setRemainingMinutes(undefined);
      setHasActiveReservation(false);

      const targetAreaIds = overrideAreaIds || watch('areaIds');

      const timeSlotParams = {
        area_ids: targetAreaIds,
        date: date,
      };

      const result = await fetchPrivateRoomTimeSlots(timeSlotParams);
      if (result.ok) {
        setSpaces(result.data.spaces);
        setRemainingMinutes(result.data.remaining_minutes);
        setHasActiveReservation(result.data.has_active_reservation);
      } else {
        console.error(
          'Failed to fetch private room time slots:',
          result.error,
          result.details,
        );
        setSpaces([]);
        setRemainingMinutes(undefined);
        setHasActiveReservation(false);
        setErrorKey(result.error ?? null);
      }
    },
    [setValue, watch, fetchPrivateRoomTimeSlots],
  );

  const onSubmit = (data: ReservationFormData) => {
    const reservationConfirmData = {
      serviceType: SPACE_TYPE.PRIVATE_ROOM,
      airport: data.airport,
      flightType: data.flightType,
      terminal: data.terminal,
      lounge: data.lounge,
      areaIds: data.areaIds,
      boardingDate: data.boardingDate,
      startDateTime: data.selectedStartTime
        ? toISO8601(data.boardingDate!, data.selectedStartTime)
        : undefined,
      endDateTime: data.selectedEndTime
        ? toISO8601(data.boardingDate!, data.selectedEndTime)
        : undefined,
      spaceId: data.selectedSpaceId, // API送信用（DBのID）
      spaceNumber: data.selectedSpaceNo, // 表示用（ブース番号）
      loungeFloor: loungeFloor ? String(loungeFloor) : undefined,
    };

    navigate(ROUTE_PATH.RESERVATION_CONFIRM, {
      state: {
        reservationData: reservationConfirmData,
      },
    });
  };

  if (errorKey) {
    return <ErrorNotice message={translateApiError(errorKey).message} />;
  }

  return (
    <form className="reservation-card__form" onSubmit={handleSubmit(onSubmit)}>
      <div
        className="reservation-card__step reservation-card__step--airport step1 is-complete"
        data-step="1"
      >
        <div className="reservation-card__step-header">
          <div className="reservation-card__step-label">
            {t('common.airport.haneda')}
          </div>
        </div>
      </div>

      <div
        className="reservation-card__step reservation-card__step--terminal step2 is-complete is-terminal-selected"
        data-step="2"
      >
        <div className="reservation-card__step-header">
          <div className="reservation-card__step-label">
            {t('common.terminalWithFlightType', {
              terminal: t('common.terminal.terminal2'),
              flightType: t('common.flightType.domestic'),
            })}
          </div>
        </div>
        <a
          href={t('reservation.terminal.linkUrlBooth')}
          target="_blank"
          rel="noopener noreferrer"
          className="reservation-card__note--link reservation-card__note--pointer"
        >
          {t('reservation.terminal.linkText')}
        </a>
      </div>

      <div
        className="reservation-card__step reservation-card__step--lounge step3 is-complete is-lounge-selected"
        data-step="3"
      >
        <div className="reservation-card__step-header">
          <div
            className="reservation-card__step-label"
            data-initial-label={t('reservation.lounge.label')}
          >
            <img
              className="reservation-card__lounge-label-img reservation-card__lounge-label-img--suite"
              src="/Common_ANA_SUITELOUNGE.png"
              alt="ANA SUITE LOUNGE"
            />
          </div>
        </div>
        <a
          href={t('reservation.lounge.linkUrlBooth')}
          target="_blank"
          rel="noopener noreferrer"
          className="reservation-card__link"
        >
          {t('reservation.lounge.linkText')}
        </a>
      </div>

      <div
        className={`reservation-card__step reservation-card__step--datetime step4 ${isSectionOpen ? 'is-open' : isSectionComplete ? 'is-active is-complete' : ''}`}
        data-step="4"
        onClick={() => setIsSectionOpen(true)}
      >
        <ReservationFormPrivateRoomSection
          key={boardingDate}
          defaultVal={{
            date: boardingDate,
            restoreDate: initialRestoreData,
          }}
          handleDateSelect={handleDateSelect}
          spaces={spaces}
          remainingMinutes={remainingMinutes}
          hasActiveReservation={hasActiveReservation}
          setValue={setValue}
          setLoungeFloor={setLoungeFloor}
          // レンダリングタイミングの関係でsetTimeoutが必要
          close={() => setTimeout(() => setIsSectionOpen(false))}
          confirmedDateStr={
            !isSectionOpen && confirmedDateStr ? confirmedDateStr : ''
          }
        ></ReservationFormPrivateRoomSection>
      </div>

      {!isSectionOpen && isSectionComplete && (
        <div className="reservation-card__confirm reservation-card__confirm--booth">
          <button type="submit" className="reservation-card__confirm-button">
            {t('reservation.confirmContent')}
          </button>
          <p className="reservation-card__confirm-note">
            {t('reservation.confirm.note')}
          </p>
        </div>
      )}
    </form>
  );
};
